(function () {
  const SOURCE = 'promo-campaigns';
  const PROP_FLAG = '_promo_gift';
  const PROP_CAMPAIGN = '_promo_campaign_id';
  const PROP_LINK_SKU = '_promo_qualifying_sku';

  const cfgEl = document.querySelector('[data-promo-campaigns-config]');
  if (!cfgEl) return;

  let payload;
  try {
    payload = JSON.parse(cfgEl.textContent.trim());
  } catch (e) {
    return;
  }

  const rawCampaigns = Array.isArray(payload && payload.campaigns) ? payload.campaigns : [];
  const campaigns = rawCampaigns.map(normalizeCampaign).filter(Boolean);
  if (!campaigns.length) return;
  campaigns.sort((a, b) => (b.priority || 0) - (a.priority || 0));

  const escapeDiv = document.createElement('div');
  const htmlParser = new DOMParser();
  const propCache = new WeakMap();

  const GIFT_FAILURE_COOLDOWN_MS = 5000;
  const CART_RECONCILE_RETRY_MS = 150;
  const giftFailureGuards = new Map();

  let cartCache = null;
  let cartFetchPromise = null;
  let isEnforcing = false;
  let needsReevaluation = false;
  let evaluateTimer = null;
  let pendingCartHint = null;
  let lastUiKey = '';
  const lastFillPct = new Map();
  let chooseQueue = [];
  let chooseContext = null;
  let selectedChooseVariantId = null;
  let chooseModalOpen = false;
  let modalSessionActive = false;
  let previousBodyOverflow = '';
  let modalFocusReturn = null;

  const modal = document.getElementById('promo-campaigns-success-modal');
  const modalClose = document.getElementById('promo-campaigns-success-close');

  const R = typeof routes !== 'undefined' ? routes : {};
  const ajaxCartUpdateUrl = ensureCartAjaxUrl(R.cart_update_url, '/cart/update');
  const ajaxCartAddUrl = ensureCartAjaxUrl(R.cart_add_url, '/cart/add');

  function shopifyRoot() {
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return String(window.Shopify.routes.root);
    }
    return '/';
  }

  function localePath(path) {
    const root = shopifyRoot().replace(/\/$/, '');
    const normalized = String(path).startsWith('/') ? String(path) : `/${path}`;
    if (!root || root === '/') return normalized;
    return `${root}${normalized}`;
  }

  function cartPageUrl() {
    if (R.cart_url) return String(R.cart_url).replace(/\/$/, '').split('?')[0];
    return localePath('/cart');
  }

  function cartJsUrl() {
    if (R.cart_url) {
      const base = String(R.cart_url).replace(/\/$/, '').split('?')[0];
      return base.endsWith('.js') ? base : `${base}.js`;
    }
    return localePath('/cart.js');
  }

  function normalizeCampaign(raw) {
    if (!raw || !raw.enabled || !raw.id || !raw.type) return null;
    const priority = Number(raw.priority) || 0;
    const stackable = raw.stackable === true;
    const skuSet = buildSkuSet(raw.qualifyingSkus);
    if (skuSet.size === 0 && raw.type !== 'spend_discount') return null;

    if (raw.type === 'qty_separate_gift' || raw.type === 'qty_same_sku_gift') {
      const minQty = parsePositiveInt(raw.minimumQuantity);
      if (minQty == null) return null;
      const giftQuantity =
        raw.giftQuantity == null || raw.giftQuantity === ''
          ? 1
          : parsePositiveInt(raw.giftQuantity);
      if (giftQuantity == null) return null;
      if (raw.type === 'qty_separate_gift') {
        const giftVariants = normalizeGiftVariants(raw.giftVariants);
        const giftSelection =
          raw.giftSelection === 'choose' || (!Number(raw.giftVariantId) && giftVariants.length > 1)
            ? 'choose'
            : 'auto';
        const giftVariantId = Number(raw.giftVariantId) || 0;
        if (giftSelection === 'auto' && !giftVariantId) return null;
        if (giftSelection === 'choose' && giftVariants.length === 0) return null;
        return {
          id: String(raw.id),
          type: raw.type,
          priority,
          stackable,
          skuSet,
          minQty,
          giftQuantity,
          quantityMode: raw.quantityMode === 'combined' ? 'combined' : 'per_sku',
          giftSelection,
          giftVariantId,
          giftVariants,
          allowedVariantIds: new Set(
            giftSelection === 'auto'
              ? [giftVariantId]
              : giftVariants.map((v) => v.id).filter(Boolean)
          ),
          giftSku: String(raw.giftSku || '').trim(),
          giftProductTitle: String(raw.giftProductTitle || '').trim(),
          giftImage: raw.giftImage || '',
          reminderMessage: String(raw.reminderMessage || '').trim(),
          reachedMessage: String(raw.reachedMessage || '').trim(),
        };
      }
      return {
        id: String(raw.id),
        type: raw.type,
        priority,
        stackable,
        skuSet,
        minQty,
        giftQuantity,
        quantityMode: raw.quantityMode === 'combined' ? 'combined' : 'per_sku',
        reminderMessage: String(raw.reminderMessage || '').trim(),
        reachedMessage: String(raw.reachedMessage || '').trim(),
      };
    }

    if (raw.type === 'spend_discount') {
      const minCents = parseMinCents(raw.minimumSpendRaw);
      const discountCode = String(raw.discountCode || '').trim();
      if (minCents == null || minCents <= 0 || !discountCode) return null;
      return {
        id: String(raw.id),
        type: raw.type,
        priority,
        stackable,
        skuSet,
        minCents,
        discountCode,
        progressMessage:
          String(raw.progressMessage || '').trim() ||
          "You're [[remaining]] away from unlocking your discount!",
        reachedMessage:
          String(raw.reachedMessage || '').trim() ||
          'You\'ve unlocked your discount! Use code [[code]] at checkout.',
        progressSubtext: String(raw.progressSubtext || '').trim(),
      };
    }

    if (raw.type === 'spend_separate_gift') {
      const minCents = parseMinCents(raw.minimumSpendRaw);
      const giftVariants = normalizeGiftVariants(raw.giftVariants);
      const giftSelection =
        raw.giftSelection === 'choose' || (!Number(raw.giftVariantId) && giftVariants.length > 1)
          ? 'choose'
          : 'auto';
      const giftVariantId = Number(raw.giftVariantId) || 0;
      if (minCents == null || minCents <= 0) return null;
      if (giftSelection === 'auto' && !giftVariantId) return null;
      if (giftSelection === 'choose' && giftVariants.length === 0) return null;
      return {
        id: String(raw.id),
        type: raw.type,
        priority,
        stackable,
        skuSet,
        minCents,
        giftSelection,
        giftVariantId,
        giftVariants,
        allowedVariantIds: new Set(
          giftSelection === 'auto' ? [giftVariantId] : giftVariants.map((v) => v.id).filter(Boolean)
        ),
        giftProductId: Number(raw.giftProductId || 0),
        giftSku: String(raw.giftSku || '').trim(),
        giftProductTitle: String(raw.giftProductTitle || '').trim(),
        giftImage: raw.giftImage || '',
        progressMessage:
          String(raw.progressMessage || '').trim() ||
          "You're [[remaining]] away from earning a free gift!",
        reachedMessage:
          String(raw.reachedMessage || '').trim() || 'Congratulations! You earned a free gift.',
        progressSubtext: String(raw.progressSubtext || '').trim(),
      };
    }

    return null;
  }

  function parsePositiveInt(raw) {
    const value = String(raw ?? '').trim();
    if (!/^\d+$/.test(value)) return null;
    const n = Number(value);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  }

  function normalizeGiftVariants(list) {
    if (!Array.isArray(list)) return [];
    return list
      .map((v) => ({
        id: Number(v && v.id) || 0,
        sku: String((v && v.sku) || '').trim(),
        title: String((v && v.title) || '').trim(),
        available: v && v.available !== false,
        image: (v && v.image) || '',
      }))
      .filter((v) => v.id)
      .sort(compareGiftVariantsAsc);
  }

  function giftVariantSizeRank(variant) {
    const SIZE_RANK = {
      xxs: 10,
      xs: 20,
      s: 30,
      sm: 30,
      small: 30,
      m: 40,
      md: 40,
      medium: 40,
      l: 50,
      lg: 50,
      large: 50,
      xl: 60,
      xxl: 70,
      '2xl': 70,
      '3xl': 80,
    };
    const title = String(variant.title || '')
      .toLowerCase()
      .trim();
    if (SIZE_RANK[title] != null) return SIZE_RANK[title];

    const sku = String(variant.sku || '').toLowerCase();
    const fromSku = sku.match(/(?:^|[\s+\-_])(xxs|xs|xxl|xl|sm|md|lg|small|medium|large|s|m|l)(?:\s*$)/i);
    if (fromSku && SIZE_RANK[fromSku[1].toLowerCase()] != null) {
      return SIZE_RANK[fromSku[1].toLowerCase()];
    }
    return null;
  }

  function compareGiftVariantsAsc(a, b) {
    const rankA = giftVariantSizeRank(a);
    const rankB = giftVariantSizeRank(b);
    if (rankA != null && rankB != null && rankA !== rankB) return rankA - rankB;

    const skuCmp = String(a.sku || '').localeCompare(String(b.sku || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    if (skuCmp !== 0) return skuCmp;

    return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  function parseMinCents(raw) {
    const cleaned = String(raw ?? '')
      .trim()
      .replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    const n = parseFloat(cleaned);
    if (Number.isNaN(n) || n <= 0) return null;
    return Math.round(n * 100);
  }

  function buildSkuSet(qualifyingSkus) {
    const s = new Set();
    String(qualifyingSkus || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .forEach((x) => s.add(x));
    return s;
  }

  function ensureCartAjaxUrl(routePath, fallbackPath) {
    const raw = routePath || localePath(fallbackPath);
    const qIndex = String(raw).indexOf('?');
    const pathPart = qIndex === -1 ? String(raw) : String(raw).slice(0, qIndex);
    const query = qIndex === -1 ? '' : String(raw).slice(qIndex);
    const noTrail = pathPart.replace(/\/$/, '');
    const withJs = noTrail.endsWith('.js') ? noTrail : `${noTrail}.js`;
    return withJs + query;
  }

  function normalizeProperties(item) {
    if (propCache.has(item)) return propCache.get(item);
    const p = item.properties;
    let result;
    if (!p) result = {};
    else if (Array.isArray(p)) {
      const o = {};
      p.forEach((row) => {
        if (row && row.name) o[String(row.name)] = row.value;
      });
      result = o;
    } else result = typeof p === 'object' ? p : {};
    propCache.set(item, result);
    return result;
  }

  function isFullCartPayload(data) {
    // /cart/add.js returns { items: [...] } for added lines only — require a real cart token.
    return Boolean(data && Array.isArray(data.items) && typeof data.token === 'string' && data.token);
  }

  function normalizeEventCart(cartLike) {
    return isFullCartPayload(cartLike) ? cartLike : null;
  }

  async function fetchCart() {
    const res = await fetch(cartJsUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function getCart(force) {
    if (!force && cartCache) return cartCache;
    if (cartFetchPromise) return cartFetchPromise;

    cartFetchPromise = fetchCart()
      .then(function (cart) {
        cartCache = cart;
        return cart;
      })
      .finally(function () {
        cartFetchPromise = null;
      });

    return cartFetchPromise;
  }

  function setCartCache(cart) {
    if (isFullCartPayload(cart)) cartCache = cart;
  }

  function invalidateCartCache() {
    cartCache = null;
    lastUiKey = '';
  }

  const campaignsByType = new Map();
  campaigns.forEach((campaign) => {
    let list = campaignsByType.get(campaign.type);
    if (!list) {
      list = [];
      campaignsByType.set(campaign.type, list);
    }
    list.push(campaign);
  });
  const qtyCampaigns = [
    ...(campaignsByType.get('qty_separate_gift') || []),
    ...(campaignsByType.get('qty_same_sku_gift') || []),
  ];
  const spendCampaigns = [
    ...(campaignsByType.get('spend_separate_gift') || []),
    ...(campaignsByType.get('spend_discount') || []),
  ];
  const moneyFormatters = new Map();

  function isGiftLineForCampaign(item, campaign) {
    const props = normalizeProperties(item);
    if (props[PROP_FLAG] !== 'true' && props[PROP_FLAG] !== true) return false;
    return String(props[PROP_CAMPAIGN] || '') === campaign.id;
  }

  function getLinkSku(item, campaign) {
    const props = normalizeProperties(item);
    const linkSku = props[PROP_LINK_SKU] != null ? String(props[PROP_LINK_SKU]).trim() : '';
    if (linkSku && campaign.skuSet.has(linkSku)) return linkSku;
    return '';
  }

  function giftLineProperties(campaign, linkSku) {
    const props = {
      [PROP_FLAG]: 'true',
      [PROP_CAMPAIGN]: campaign.id,
    };
    if (linkSku) props[PROP_LINK_SKU] = linkSku;
    return props;
  }

  function analyzeQtyCampaign(cart, campaign) {
    const qualifyingBySku = new Map();
    const giftLines = [];
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (isGiftLineForCampaign(item, campaign)) {
        giftLines.push({
          itemIndex: i,
          key: item.key ? String(item.key) : '',
          variantId: Number(item.variant_id || 0),
          quantity: item.quantity || 0,
          sku: getLinkSku(item, campaign),
        });
        continue;
      }
      const sku = item.sku != null ? String(item.sku).trim() : '';
      if (!sku || !campaign.skuSet.has(sku)) continue;
      let existing = qualifyingBySku.get(sku);
      if (!existing) {
        existing = { quantity: 0, variantId: Number(item.variant_id || 0) || null };
        qualifyingBySku.set(sku, existing);
      } else if (!existing.variantId && item.variant_id) {
        existing.variantId = Number(item.variant_id);
      }
      existing.quantity += item.quantity || 0;
    }

    let totalQty = 0;
    qualifyingBySku.forEach((entry) => {
      totalQty += entry.quantity || 0;
    });

    if (campaign.quantityMode === 'combined') {
      const qualifies = totalQty >= campaign.minQty;
      const expectedGiftQty = qualifies
        ? Math.floor(totalQty / campaign.minQty) * campaign.giftQuantity
        : 0;
      const giftQtyPresent = giftLines.reduce((sum, g) => sum + (g.quantity || 0), 0);
      const eligibleTargets =
        expectedGiftQty > 0
          ? [
              {
                sku: '',
                quantity: expectedGiftQty,
                variantId: 0,
                expectedGiftQty,
              },
            ]
          : [];

      return {
        qualifyingBySku,
        giftLines,
        eligibleTargets,
        totalQty,
        expectedGiftQty,
        giftQtyPresent,
        giftPresent: giftLines.length > 0,
        qualifies,
        mode: 'combined',
      };
    }

    const eligibleTargets = [];
    qualifyingBySku.forEach((entry, sku) => {
      if (!entry || entry.quantity < campaign.minQty) return;
      eligibleTargets.push({
        sku,
        quantity: entry.quantity,
        variantId: Number(entry.variantId || 0),
      });
    });
    eligibleTargets.sort((a, b) => (a.sku > b.sku ? 1 : a.sku < b.sku ? -1 : 0));

    return {
      qualifyingBySku,
      giftLines,
      eligibleTargets,
      totalQty,
      giftPresent: giftLines.length > 0,
      qualifies: eligibleTargets.length > 0,
      mode: 'per_sku',
    };
  }

  function analyzeSpendCampaign(cart, campaign) {
    let giftPresent = false;
    let giftLines = [];
    let skuOk = false;
    let subtotal = 0;
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (isGiftLineForCampaign(item, campaign)) {
        giftPresent = true;
        giftLines.push({
          itemIndex: i,
          key: item.key ? String(item.key) : '',
          variantId: Number(item.variant_id || 0),
          quantity: item.quantity || 0,
        });
        continue;
      }
      const sku = item.sku != null ? String(item.sku).trim() : '';
      const matchesSku = campaign.skuSet.size === 0 || (sku && campaign.skuSet.has(sku));
      if (matchesSku) {
        skuOk = true;
        if (!(campaign.giftSku && sku === campaign.giftSku)) {
          subtotal += item.final_line_price != null ? item.final_line_price : item.line_price || 0;
        }
      }
    }

    const meetsSpend = subtotal >= campaign.minCents;
    return {
      giftPresent,
      giftLines,
      skuOk,
      subtotal,
      meetsSpend,
      qualifies: skuOk && meetsSpend,
    };
  }

  function planQtyCorrections(campaign, state) {
    if (campaign.quantityMode === 'combined' || state.mode === 'combined') {
      const expected = state.expectedGiftQty || 0;
      const updates = {};
      let keptQty = 0;

      for (const giftLine of state.giftLines) {
        if (!giftLine.key) continue;
        const variantOk =
          campaign.type === 'qty_same_sku_gift'
            ? true
            : campaign.allowedVariantIds
              ? campaign.allowedVariantIds.has(giftLine.variantId)
              : giftLine.variantId === campaign.giftVariantId;

        if (!variantOk || keptQty >= expected) {
          updates[giftLine.key] = 0;
          continue;
        }

        const keepQty = Math.min(giftLine.quantity || 0, expected - keptQty);
        keptQty += keepQty;
        if (keepQty !== giftLine.quantity) {
          updates[giftLine.key] = keepQty;
        }
      }

      const shortfall = expected - keptQty;
      const toAdd =
        shortfall > 0
          ? [
              {
                sku: '',
                quantity: shortfall,
                variantId: 0,
                expectedGiftQty: shortfall,
              },
            ]
          : [];
      return { updates, toAdd };
    }

    const updates = {};
    const targetsBySku = new Map(state.eligibleTargets.map((t) => [t.sku, t]));
    const keptSku = new Set();

    for (const giftLine of state.giftLines) {
      if (!giftLine.key) continue;
      const target = targetsBySku.get(giftLine.sku);
      const variantOk =
        campaign.type === 'qty_same_sku_gift'
          ? Boolean(target && giftLine.variantId === target.variantId)
          : campaign.allowedVariantIds
            ? campaign.allowedVariantIds.has(giftLine.variantId)
            : giftLine.variantId === campaign.giftVariantId;
      const shouldKeep =
        target &&
        variantOk &&
        giftLine.quantity === campaign.giftQuantity &&
        giftLine.sku === target.sku &&
        !keptSku.has(giftLine.sku);

      if (shouldKeep) keptSku.add(giftLine.sku);
      else updates[giftLine.key] = 0;
    }

    const toAdd = [];
    state.eligibleTargets.forEach((target) => {
      if (!keptSku.has(target.sku)) toAdd.push(target);
    });
    return { updates, toAdd };
  }

  function createMutationPlan() {
    return {
      updates: {},
      additions: [],
      chooseEntries: [],
      modalCampaign: null,
      changed: false,
      didAdd: false,
      claimedSkus: new Set(),
    };
  }

  function mergePlanUpdates(plan, updates) {
    if (!updates) return;
    Object.keys(updates).forEach((key) => {
      const nextQty = Number(updates[key]);
      if (!Object.prototype.hasOwnProperty.call(plan.updates, key)) {
        plan.updates[key] = nextQty;
        plan.changed = true;
        return;
      }
      // Prefer removal / lower qty when campaigns disagree on the same line.
      plan.updates[key] = Math.min(Number(plan.updates[key]), nextQty);
      plan.changed = true;
    });
  }

  function appendPlanAdditions(plan, items, campaign) {
    if (!items || !items.length) return;
    items.forEach((item) => plan.additions.push(item));
    plan.changed = true;
    plan.didAdd = true;
    if (!plan.modalCampaign) plan.modalCampaign = campaign;
  }

  function mapQtyAdditions(campaign, toAdd) {
    return toAdd
      .map((target) => {
        const variantId =
          campaign.type === 'qty_separate_gift' ? campaign.giftVariantId : target.variantId;
        const qty =
          campaign.quantityMode === 'combined'
            ? Number(target.quantity || target.expectedGiftQty || campaign.giftQuantity)
            : campaign.giftQuantity;
        return {
          id: variantId,
          quantity: qty,
          properties: giftLineProperties(campaign, target.sku || undefined),
        };
      })
      .filter((row) => row.id && row.quantity > 0);
  }

  function campaignHasExclusiveConflict(campaign, claimedSkus) {
    if (campaign.stackable || !claimedSkus || claimedSkus.size === 0) return false;
    for (const sku of campaign.skuSet) {
      if (claimedSkus.has(sku)) return true;
    }
    return false;
  }

  function markCampaignClaimedSkus(campaign, state, claimedSkus) {
    if (campaign.stackable || !state.qualifies) return;
    if (campaign.type === 'spend_separate_gift') {
      if (state.skuOk) campaign.skuSet.forEach((sku) => claimedSkus.add(sku));
      return;
    }
    if (state.mode === 'combined' || campaign.quantityMode === 'combined') {
      campaign.skuSet.forEach((sku) => claimedSkus.add(sku));
      return;
    }
    state.eligibleTargets.forEach((target) => {
      if (target.sku) claimedSkus.add(target.sku);
    });
  }

  function removeCampaignGiftLines(state, plan) {
    if (!state.giftPresent) return;
    const updates = {};
    state.giftLines.forEach((giftLine) => {
      if (giftLine.key) updates[giftLine.key] = 0;
    });
    mergePlanUpdates(plan, updates);
  }

  function contributeQtyCampaign(cart, campaign, plan) {
    const state = analyzeQtyCampaign(cart, campaign);
    if (campaignHasExclusiveConflict(campaign, plan.claimedSkus)) {
      removeCampaignGiftLines(state, plan);
      return;
    }
    if (!state.qualifies) {
      if (!state.giftPresent) return;
      const updates = {};
      state.giftLines.forEach((g) => {
        if (g.key) updates[g.key] = 0;
      });
      mergePlanUpdates(plan, updates);
      return;
    }

    const { updates, toAdd } = planQtyCorrections(campaign, state);
    mergePlanUpdates(plan, updates);

    if (!toAdd.length) {
      markCampaignClaimedSkus(campaign, state, plan.claimedSkus);
      return;
    }

    if (campaign.type === 'qty_separate_gift' && campaign.giftSelection === 'choose') {
      plan.chooseEntries.push({ campaign, targets: toAdd });
      markCampaignClaimedSkus(campaign, state, plan.claimedSkus);
      return;
    }

    appendPlanAdditions(plan, mapQtyAdditions(campaign, toAdd), campaign);
    markCampaignClaimedSkus(campaign, state, plan.claimedSkus);
  }

  function contributeSpendCampaign(cart, campaign, plan) {
    const state = analyzeSpendCampaign(cart, campaign);
    if (campaignHasExclusiveConflict(campaign, plan.claimedSkus)) {
      removeCampaignGiftLines(state, plan);
      return;
    }

    if (!state.qualifies) {
      if (!state.giftPresent) return;
      const updates = {};
      state.giftLines.forEach((g) => {
        if (g.key) updates[g.key] = 0;
      });
      mergePlanUpdates(plan, updates);
      return;
    }

    if (state.giftPresent) {
      const updates = {};
      let kept = false;
      for (const g of state.giftLines) {
        if (!g.key) continue;
        const variantOk = campaign.allowedVariantIds
          ? campaign.allowedVariantIds.has(g.variantId)
          : g.variantId === campaign.giftVariantId;
        if (!kept && variantOk && g.quantity === 1) {
          kept = true;
        } else {
          updates[g.key] = 0;
        }
      }
      mergePlanUpdates(plan, updates);
      markCampaignClaimedSkus(campaign, state, plan.claimedSkus);
      return;
    }

    if (campaign.giftSelection === 'choose') {
      plan.chooseEntries.push({ campaign, targets: [{ sku: '' }] });
      markCampaignClaimedSkus(campaign, state, plan.claimedSkus);
      return;
    }

    appendPlanAdditions(
      plan,
      [
        {
          id: campaign.giftVariantId,
          quantity: 1,
          properties: giftLineProperties(campaign),
        },
      ],
      campaign
    );
    markCampaignClaimedSkus(campaign, state, plan.claimedSkus);
  }

  function buildMutationPlan(cart) {
    // Higher-priority campaigns run first; exclusive campaigns claim qualifying SKUs.
    const plan = createMutationPlan();
    campaigns.forEach((campaign) => {
      if (campaign.type === 'spend_discount') return;
      if (campaign.type === 'spend_separate_gift') {
        contributeSpendCampaign(cart, campaign, plan);
      } else {
        contributeQtyCampaign(cart, campaign, plan);
      }
    });
    return plan;
  }

  function giftAttemptKey(items) {
    return (items || [])
      .map((item) => {
        const props = item.properties || {};
        return `${item.id}:${item.quantity}:${JSON.stringify(props)}`;
      })
      .join('|');
  }

  function shouldAttemptGiftAdd(items) {
    const key = giftAttemptKey(items);
    const failedAt = giftFailureGuards.get(key);
    if (failedAt && Date.now() - failedAt < GIFT_FAILURE_COOLDOWN_MS) return false;
    return true;
  }

  function recordGiftAddFailure(items) {
    giftFailureGuards.set(giftAttemptKey(items), Date.now());
  }

  function clearGiftAddFailure(items) {
    giftFailureGuards.delete(giftAttemptKey(items));
  }

  function getCartPageSectionIds() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (!mainCartItems || !mainCartFooter) return null;
    const itemsSectionId = mainCartItems.dataset.id;
    const footerSectionId = mainCartFooter.dataset.id;
    if (!itemsSectionId || !footerSectionId) return null;
    return { itemsSectionId, footerSectionId };
  }

  function getBundledSectionsRequest() {
    if (!isCartPageContext()) return null;
    const ids = getCartPageSectionIds();
    if (!ids) return null;
    return {
      sections: `${ids.itemsSectionId},${ids.footerSectionId}`,
      sections_url: cartPageUrl(),
    };
  }

  function applyCartPageSectionHtml(itemsHtml, footerHtml) {
    if (!itemsHtml || !footerHtml) return false;
    const itemsDoc = htmlParser.parseFromString(itemsHtml, 'text/html');
    const footerDoc = htmlParser.parseFromString(footerHtml, 'text/html');
    const cartItemsContainer = document.querySelector('cart-items .js-contents');
    const newItemsHtml = queryInnerHTML(itemsDoc, 'cart-items .js-contents');
    if (cartItemsContainer && newItemsHtml) cartItemsContainer.innerHTML = newItemsHtml;
    const footerContainer = document.querySelector('#main-cart-footer .js-contents');
    const newFooterHtml = queryInnerHTML(footerDoc, '#main-cart-footer .js-contents');
    if (footerContainer && newFooterHtml) footerContainer.innerHTML = newFooterHtml;
    return Boolean(cartItemsContainer && newItemsHtml && footerContainer && newFooterHtml);
  }

  function applyBundledSectionsFromPayload(sections) {
    const ids = getCartPageSectionIds();
    if (!ids || !sections) return false;
    return applyCartPageSectionHtml(sections[ids.itemsSectionId], sections[ids.footerSectionId]);
  }

  function noteMutationSectionsApplied(response) {
    if (response && response.sections && applyBundledSectionsFromPayload(response.sections)) {
      return true;
    }
    return false;
  }

  function additionsPresentInCart(cart, additions) {
    if (!additions || !additions.length) return true;
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    return additions.every((addition) => {
      const variantId = Number(addition.id);
      const expectedQty = Number(addition.quantity) || 1;
      const props = addition.properties || {};
      return items.some((item) => {
        if (Number(item.variant_id) !== variantId) return false;
        const itemProps = normalizeProperties(item);
        if (itemProps[PROP_FLAG] !== 'true' && itemProps[PROP_FLAG] !== true) return false;
        if (
          props[PROP_CAMPAIGN] &&
          String(itemProps[PROP_CAMPAIGN] || '') !== String(props[PROP_CAMPAIGN])
        ) {
          return false;
        }
        return (item.quantity || 0) >= expectedQty;
      });
    });
  }

  async function getCartAfterMutation(verifyFn) {
    let cart = await getCart(true);
    if (!verifyFn || verifyFn(cart)) return cart;
    await new Promise((resolve) => setTimeout(resolve, CART_RECONCILE_RETRY_MS));
    invalidateCartCache();
    if (cartFetchPromise) {
      try {
        await cartFetchPromise;
      } catch (e) {
        /* ignore */
      }
      cartFetchPromise = null;
    }
    return getCart(true);
  }

  async function applyCartUpdates(updates) {
    if (!updates || Object.keys(updates).length === 0) return null;
    const body = { updates };
    const bundled = getBundledSectionsRequest();
    if (bundled) {
      body.sections = bundled.sections;
      body.sections_url = bundled.sections_url;
    }
    const res = await fetch(ajaxCartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
    if (!res.ok) {
      console.warn('[promo-campaigns] update failed', await res.text());
      throw new Error('cart update failed');
    }
    let updated;
    try {
      updated = await res.json();
    } catch (e) {
      throw new Error('cart update returned invalid response');
    }
    if (!isFullCartPayload(updated)) {
      throw new Error('cart update returned invalid response');
    }
    setCartCache(updated);
    return {
      cart: updated,
      sectionsApplied: noteMutationSectionsApplied(updated),
    };
  }

  async function addGiftItems(items) {
    if (!items || !items.length) return null;
    const body = { items };
    const bundled = getBundledSectionsRequest();
    if (bundled) {
      body.sections = bundled.sections;
      body.sections_url = bundled.sections_url;
    }
    const response = await fetch(ajaxCartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      console.warn('[promo-campaigns] bad JSON', e);
    }
    if (!response.ok) {
      console.warn('[promo-campaigns] add failed', data);
      const err = new Error('add gift failed');
      err.status = response.status;
      err.payload = data;
      throw err;
    }
    const sectionsApplied = noteMutationSectionsApplied(data);
    return { sectionsApplied };
  }

  async function executeMutationPlan(cart, plan) {
    let current = cart;
    let sectionsApplied = false;
    const hasUpdates = plan.updates && Object.keys(plan.updates).length > 0;
    const hasAdditions = plan.additions && plan.additions.length > 0;

    if (hasUpdates) {
      const updateResult = await applyCartUpdates(plan.updates);
      current = updateResult.cart;
      sectionsApplied = updateResult.sectionsApplied;
    }

    if (hasAdditions) {
      if (!shouldAttemptGiftAdd(plan.additions)) {
        console.warn('[promo-campaigns] skipping gift add after recent failure');
        return { cart: current, sectionsApplied };
      }
      try {
        const addResult = await addGiftItems(plan.additions);
        sectionsApplied = sectionsApplied || addResult.sectionsApplied;
        clearGiftAddFailure(plan.additions);
        current = await getCartAfterMutation((nextCart) =>
          additionsPresentInCart(nextCart, plan.additions)
        );
      } catch (err) {
        recordGiftAddFailure(plan.additions);
        throw err;
      }
    } else if (plan.changed && !isFullCartPayload(current)) {
      current = await getCart(true);
    }

    return { cart: current, sectionsApplied };
  }

  function escapeHtml(str) {
    escapeDiv.textContent = str == null ? '' : String(str);
    return escapeDiv.innerHTML;
  }

  function campaignText(str) {
    return str == null ? '' : String(str);
  }

  function fillQtyReminder(template, remainingQty) {
    const remaining = String(Math.max(0, remainingQty));
    const parts = String(template).split('[[remaining_qty]]');
    if (parts.length === 1) return escapeHtml(template);
    return parts.map(escapeHtml).join(`<strong>${escapeHtml(remaining)}</strong>`);
  }

  function formatMoney(cents, currencyCode) {
    const code = currencyCode || 'USD';
    let formatter = moneyFormatters.get(code);
    if (!formatter) {
      try {
        formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: code });
      } catch (e) {
        formatter = null;
      }
      moneyFormatters.set(code, formatter);
    }
    if (!formatter) return `$${(cents / 100).toFixed(2)}`;
    return formatter.format(cents / 100);
  }

  function fillCodeTemplate(template, code) {
    const safeCode = `<strong class="promo-campaign-progress__code">${escapeHtml(code)}</strong>`;
    const parts = String(template).split('[[code]]');
    if (parts.length === 1) {
      const text = escapeHtml(template).trim();
      return text ? `${text} ${safeCode}` : safeCode;
    }
    return parts.map(escapeHtml).join(safeCode);
  }

  function fillSpendTemplate(template, remainingFormatted) {
    const parts = String(template).split('[[remaining]]');
    if (parts.length === 1) return escapeHtml(template);
    return parts
      .map(escapeHtml)
      .join(`<strong>${escapeHtml(remainingFormatted)}</strong>`);
  }

  function findByCampaignId(nodes, campaignId) {
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getAttribute('data-promo-campaign-id') === campaignId) return nodes[i];
    }
    return null;
  }

  function ensureLineReminderHost(host, campaign) {
    let el = findByCampaignId(host.querySelectorAll('[data-promo-root]'), campaign.id);
    if (el) return el;
    el = document.createElement('div');
    el.className = 'promo-campaign-reminder promo-campaign-reminder--line';
    el.setAttribute('data-promo-root', '');
    el.setAttribute('data-promo-campaign-id', campaign.id);
    el.setAttribute('hidden', '');
    const p = document.createElement('p');
    p.className = 'promo-campaign-reminder__message';
    p.setAttribute('data-promo-primary', '');
    el.appendChild(p);
    host.appendChild(el);
    return el;
  }

  function updateQtyLineReminders(cart, sharedAnalyses) {
    const hosts = document.querySelectorAll('[data-promo-line-host]');
    if (!hosts.length || !qtyCampaigns.length) return;

    const analyses = sharedAnalyses || new Map();
    if (!sharedAnalyses) {
      qtyCampaigns.forEach((campaign) => {
        analyses.set(campaign.id, analyzeQtyCampaign(cart, campaign));
      });
    }

    hosts.forEach((host) => {
      const sku = (host.dataset.promoSku || '').trim();
      qtyCampaigns.forEach((campaign) => {
        const root = ensureLineReminderHost(host, campaign);
        const primary = root.querySelector('[data-promo-primary]');
        if (!primary) return;

        if (!sku || !campaign.skuSet.has(sku)) {
          root.setAttribute('hidden', '');
          return;
        }

        const analysis = analyses.get(campaign.id);
        let isReached;
        let remainingQty;

        if (campaign.quantityMode === 'combined') {
          isReached = analysis.qualifies || analysis.giftPresent;
          remainingQty = Math.max(0, campaign.minQty - (analysis.totalQty || 0));
        } else {
          const entry = analysis.qualifyingBySku.get(sku);
          const skuQty = entry ? entry.quantity : 0;
          isReached =
            analysis.eligibleTargets.some((t) => t.sku === sku) ||
            analysis.giftLines.some((g) => g.sku === sku);
          remainingQty = Math.max(0, campaign.minQty - skuQty);
        }

        const showReached = isReached && campaign.reachedMessage !== '';
        const showReminder = !isReached && campaign.reminderMessage !== '';

        if (!showReached && !showReminder) {
          root.setAttribute('hidden', '');
          return;
        }

        root.removeAttribute('hidden');
        primary.classList.toggle('promo-campaign-reminder__message--reached', showReached);
        if (showReached) {
          primary.textContent = campaignText(campaign.reachedMessage);
        } else {
          primary.innerHTML = fillQtyReminder(campaign.reminderMessage, remainingQty);
        }
      });
    });
  }

  function ensureProgressRoot(host, campaign) {
    let el = findByCampaignId(host.querySelectorAll('[data-promo-progress-root]'), campaign.id);
    if (el) return el;
    el = document.createElement('div');
    el.className = 'promo-campaign-progress';
    el.setAttribute('data-promo-progress-root', '');
    el.setAttribute('data-promo-campaign-id', campaign.id);
    el.setAttribute('data-promo-fresh', '');
    el.innerHTML = `
      <p class="promo-campaign-progress__message" data-promo-primary></p>
      <p class="promo-campaign-progress__subtext" data-promo-subtext hidden></p>
      <div class="promo-campaign-progress__track-wrap">
        <div class="promo-campaign-progress__track" data-promo-bar role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <div class="promo-campaign-progress__fill" data-promo-fill style="width:0%"></div>
        </div>
        <div class="promo-campaign-progress__badge" data-promo-badge aria-hidden="true">
          <span class="promo-campaign-progress__badge-percent" data-promo-badge-percent>0%</span>
        </div>
      </div>`;
    host.appendChild(el);
    return el;
  }

  function writeProgress(root, fillPct) {
    const fill = root.querySelector('[data-promo-fill]');
    const bar = root.querySelector('[data-promo-bar]');
    const badge = root.querySelector('[data-promo-badge]');
    const badgePercent = root.querySelector('[data-promo-badge-percent]');
    const visualFillPct = fillPct > 0 ? Math.max(fillPct, 1) : 0;

    // Read layout dimensions BEFORE any style writes to avoid forced reflows.
    let barWidth = 0;
    let badgeHalf = 0;
    if (badge && bar) {
      barWidth = bar.offsetWidth;
      badgeHalf = badge.offsetWidth / 2;
    }

    if (fill) fill.style.width = `${visualFillPct}%`;
    if (bar) {
      bar.style.setProperty('--promo-progress-pct', `${visualFillPct}%`);
      bar.setAttribute('aria-valuenow', String(Math.round(fillPct)));
    }
    if (badge && barWidth > 0) {
      const rawLeft = (barWidth * fillPct) / 100;
      const clampedLeft = Math.max(badgeHalf, Math.min(barWidth - badgeHalf, rawLeft));
      badge.style.left = `${clampedLeft}px`;
    }
    if (badgePercent) badgePercent.textContent = `${Math.round(fillPct)}%`;
  }

  function applyProgressVisual(root, fillPct, seedPct) {
    const hasSeed = typeof seedPct === 'number' && Math.round(seedPct) !== Math.round(fillPct);
    if (!hasSeed) {
      writeProgress(root, fillPct);
      return;
    }

    const bar = root.querySelector('[data-promo-bar]');
    const badge = root.querySelector('[data-promo-badge]');
    if (bar) bar.style.transition = 'none';
    if (badge) badge.style.transition = 'none';
    writeProgress(root, seedPct);
    if (bar) bar.offsetWidth;
    if (bar) bar.style.transition = '';
    if (badge) badge.style.transition = '';
    writeProgress(root, fillPct);
  }

  function updateSpendProgress(cart, sharedAnalyses) {
    const hosts = document.querySelectorAll('[data-promo-progress-host]');
    if (!hosts.length || !spendCampaigns.length) return;

    const currency =
      cart.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const analyses = sharedAnalyses || new Map();
    if (!sharedAnalyses) {
      spendCampaigns.forEach((campaign) => {
        analyses.set(campaign.id, analyzeSpendCampaign(cart, campaign));
      });
    }

    const priorPct = new Map(lastFillPct);
    hosts.forEach((host) => {
      spendCampaigns.forEach((campaign) => {
        const root = ensureProgressRoot(host, campaign);
        const created = root.hasAttribute('data-promo-fresh');
        if (created) root.removeAttribute('data-promo-fresh');
        root.classList.toggle('promo-campaign-progress--discount', Boolean(campaign.discountCode));
        const primary = root.querySelector('[data-promo-primary]');
        const subtextEl = root.querySelector('[data-promo-subtext]');
        const state = analyses.get(campaign.id);
        const remaining = Math.max(0, campaign.minCents - state.subtotal);
        const remainingFmt = formatMoney(remaining, currency);

        let fillPct = 0;
        if (state.giftPresent || state.meetsSpend) fillPct = 100;
        else if (state.skuOk && campaign.minCents > 0) {
          fillPct = Math.min(100, (state.subtotal / campaign.minCents) * 100);
        }

        if (!state.skuOk && !state.giftPresent) {
          root.setAttribute('hidden', '');
          return;
        }
        root.removeAttribute('hidden');

        if (primary) {
          if (state.giftPresent || state.meetsSpend) {
            if (campaign.discountCode) {
              primary.innerHTML = fillCodeTemplate(campaign.reachedMessage, campaign.discountCode);
            } else {
              primary.textContent = campaignText(campaign.reachedMessage);
            }
          } else {
            primary.innerHTML = fillSpendTemplate(campaign.progressMessage, remainingFmt);
          }
        }
        if (subtextEl) {
          if (campaign.progressSubtext) {
            subtextEl.textContent = campaignText(campaign.progressSubtext);
            subtextEl.removeAttribute('hidden');
          } else {
            subtextEl.setAttribute('hidden', '');
          }
        }
        applyProgressVisual(root, fillPct, created ? priorPct.get(campaign.id) : undefined);
        lastFillPct.set(campaign.id, fillPct);
      });
    });
  }

  function buildAnalyses(cart) {
    const map = new Map();
    qtyCampaigns.forEach((campaign) => {
      map.set(campaign.id, analyzeQtyCampaign(cart, campaign));
    });
    spendCampaigns.forEach((campaign) => {
      map.set(campaign.id, analyzeSpendCampaign(cart, campaign));
    });
    return map;
  }

  function updateUi(cart, analyses, options) {
    const force = Boolean(options && options.force);
    const key = `${cart.item_count}|${cart.total_price}|${(cart.items || [])
      .map((i) => `${i.key}:${i.quantity}`)
      .join(',')}`;
    if (!force && key === lastUiKey) return;
    lastUiKey = key;
    const shared = analyses || buildAnalyses(cart);
    updateQtyLineReminders(cart, shared);
    updateSpendProgress(cart, shared);
  }

  function showModal(campaign, cart, analyses) {
    if (!modal) return;
    setModalMode('success');
    const productWrap = modal.querySelector('[data-promo-modal-product]');
    const imgEl = modal.querySelector('[data-promo-modal-image]');
    const titleEl = modal.querySelector('[data-promo-modal-title]');

    let title = campaign.giftProductTitle || '';
    let imageUrl = campaign.giftImage || '';

    if (campaign.type === 'qty_same_sku_gift') {
      const analysis =
        (analyses && analyses.get(campaign.id)) || analyzeQtyCampaign(cart, campaign);
      const gift = analysis.giftLines[0];
      if (gift) {
        const item = cart.items[gift.itemIndex];
        if (item) {
          title = item.product_title || item.title || title;
          imageUrl = item.image || (item.featured_image && item.featured_image.url) || imageUrl;
        }
      }
    }

    if (titleEl) titleEl.textContent = title;
    if (imgEl) {
      if (imageUrl) {
        imgEl.src = imageUrl;
        imgEl.alt = title;
        imgEl.removeAttribute('hidden');
      } else {
        imgEl.removeAttribute('src');
        imgEl.setAttribute('hidden', '');
      }
    }
    if (productWrap) {
      if (title || imageUrl) productWrap.removeAttribute('hidden');
      else productWrap.setAttribute('hidden', '');
    }

    openModalShell();
  }

  function setModalMode(mode) {
    if (!modal) return;
    const body = modal.querySelector('[data-promo-modal-body]');
    const title = modal.querySelector('#promo-campaigns-success-title');
    const picker = modal.querySelector('[data-promo-variant-picker]');
    const productWrap = modal.querySelector('[data-promo-modal-product]');

    if (mode === 'choose') {
      if (title) title.textContent = 'Choose your free gift';
      if (body) body.textContent = 'Select an option to add your free gift to the cart.';
      if (picker) picker.removeAttribute('hidden');
    } else {
      if (title) title.textContent = 'Your free gift was added!';
      if (body) body.textContent = 'It has been added to your cart.';
      if (picker) picker.setAttribute('hidden', '');
      selectedChooseVariantId = null;
    }
  }

  function openModalShell() {
    if (!modal) return;
    if (!modalSessionActive) {
      previousBodyOverflow = document.body.style.overflow;
      modalFocusReturn = document.activeElement;
      modalSessionActive = true;
    }
    chooseModalOpen = true;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const focusTarget =
      modalClose || modal.querySelector('[data-promo-variant-submit]') || modal;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }

  function endModalSession() {
    modalSessionActive = false;
    document.body.style.overflow = previousBodyOverflow;
    const returnFocus = modalFocusReturn;
    modalFocusReturn = null;
    if (returnFocus && typeof returnFocus.focus === 'function') {
      returnFocus.focus();
    }
  }

  function showChooseModal(campaign, targets) {
    if (!modal || !campaign || !targets || !targets.length) return;
    if (chooseModalOpen && chooseContext && chooseContext.campaign.id === campaign.id) return;

    chooseContext = { campaign, targets: targets.slice() };
    selectedChooseVariantId = null;
    setModalMode('choose');

    const productWrap = modal.querySelector('[data-promo-modal-product]');
    const imgEl = modal.querySelector('[data-promo-modal-image]');
    const titleEl = modal.querySelector('[data-promo-modal-title]');
    const optionsEl = modal.querySelector('[data-promo-variant-options]');
    const submitBtn = modal.querySelector('[data-promo-variant-submit]');

    if (titleEl) titleEl.textContent = campaign.giftProductTitle || '';
    if (imgEl) {
      if (campaign.giftImage) {
        imgEl.src = campaign.giftImage;
        imgEl.alt = campaign.giftProductTitle || '';
        imgEl.removeAttribute('hidden');
      } else {
        imgEl.removeAttribute('src');
        imgEl.setAttribute('hidden', '');
      }
    }
    if (productWrap) {
      if (campaign.giftProductTitle || campaign.giftImage) productWrap.removeAttribute('hidden');
      else productWrap.setAttribute('hidden', '');
    }

    if (optionsEl) {
      optionsEl.innerHTML = '';
      campaign.giftVariants.forEach((variant) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'promo-campaigns-variant-option';
        btn.textContent = variant.title === 'Default Title' ? variant.sku || 'Option' : variant.title;
        btn.dataset.variantId = String(variant.id);
        if (!variant.available) {
          btn.disabled = true;
          btn.classList.add('is-unavailable');
        }
        btn.addEventListener('click', () => {
          if (!variant.available) return;
          selectedChooseVariantId = variant.id;
          optionsEl.querySelectorAll('.promo-campaigns-variant-option').forEach((el) => {
            el.classList.toggle('is-selected', el === btn);
          });
          if (imgEl && variant.image) {
            imgEl.src = variant.image;
            imgEl.removeAttribute('hidden');
          }
          if (submitBtn) submitBtn.disabled = false;
        });
        optionsEl.appendChild(btn);
      });
    }
    if (submitBtn) submitBtn.disabled = true;
    openModalShell();
  }

  function showNextChooseModal() {
    if (!chooseQueue.length || chooseModalOpen) return;
    const next = chooseQueue.shift();
    if (!next) return;
    showChooseModal(next.campaign, next.targets);
  }

  async function submitChosenVariant() {
    if (!chooseContext || !selectedChooseVariantId) return;
    const { campaign, targets } = chooseContext;
    const target = targets[0];
    const giftQty =
      campaign.type === 'spend_separate_gift'
        ? 1
        : campaign.quantityMode === 'combined'
          ? Number(target.quantity || target.expectedGiftQty || campaign.giftQuantity)
          : campaign.giftQuantity;
    const linkSku = target && target.sku ? target.sku : '';
    const chosenVariantId = selectedChooseVariantId;

    hideModal({ suppressChoose: true });
    isEnforcing = true;
    const giftItems = [
      {
        id: chosenVariantId,
        quantity: giftQty,
        properties: giftLineProperties(campaign, linkSku || undefined),
      },
    ];
    try {
      if (!shouldAttemptGiftAdd(giftItems)) {
        console.warn('[promo-campaigns] skipping choose gift add after recent failure');
        return;
      }
      const addResult = await addGiftItems(giftItems);
      clearGiftAddFailure(giftItems);
      const cart = await getCartAfterMutation((nextCart) =>
        (nextCart.items || []).some((item) => {
          if (!isGiftLineForCampaign(item, campaign)) return false;
          return Number(item.variant_id) === Number(chosenVariantId);
        })
      );
      if (!isFullCartPayload(cart)) {
        throw new Error('cart missing after gift add');
      }

      const giftInCart = (cart.items || []).some((item) => {
        if (!isGiftLineForCampaign(item, campaign)) return false;
        return Number(item.variant_id) === Number(chosenVariantId);
      });
      if (!giftInCart) {
        throw new Error('gift variant not present in cart after add');
      }

      setCartCache(cart);
      const analyses = buildAnalyses(cart);
      await publishCartUpdate(cart, { skipSectionRefresh: addResult.sectionsApplied });
      dispatchCartUpdated(cart);
      updateUi(cart, analyses, { force: isCartPageContext() });
      showModal(campaign, cart, analyses);

      // Re-run with the full cart so remaining targets/campaigns can continue safely.
      needsReevaluation = false;
      pendingCartHint = cart;
    } catch (err) {
      console.warn('[promo-campaigns] choose add failed', err);
      recordGiftAddFailure(giftItems);
      try {
        const cart = await getCart(true);
        updateUi(cart);
        scheduleEvaluate(cart);
      } catch (e) {
        /* ignore */
      }
    } finally {
      isEnforcing = false;
      chooseContext = null;
      selectedChooseVariantId = null;
      if (pendingCartHint) {
        const hint = pendingCartHint;
        pendingCartHint = null;
        evaluateCart(hint);
      }
    }
  }

  function hideModal(options) {
    if (!modal) return;
    const wasSuccessModal = chooseModalOpen && !chooseContext;
    const chainChoose =
      wasSuccessModal && chooseQueue.length && !(options && options.suppressChoose);
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    chooseModalOpen = false;
    if (!chainChoose) endModalSession();
    if (options && options.suppressChoose) return;
    if (wasSuccessModal) showNextChooseModal();
  }

  if (modalClose) modalClose.addEventListener('click', hideModal);
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) hideModal();
    });
    const submitBtn = modal.querySelector('[data-promo-variant-submit]');
    if (submitBtn) submitBtn.addEventListener('click', submitChosenVariant);
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && modal.getAttribute('aria-hidden') === 'false') hideModal();
  });

  function isCartPageContext() {
    return Boolean(document.getElementById('main-cart-items') && document.getElementById('main-cart-footer'));
  }

  function queryInnerHTML(doc, selector) {
    return doc.querySelector(selector)?.innerHTML || '';
  }

  async function refreshCartPageSections() {
    const ids = getCartPageSectionIds();
    if (!ids) return;

    const sectionsParam = [ids.itemsSectionId, ids.footerSectionId]
      .map((id) => encodeURIComponent(id))
      .join(',');
    const res = await fetch(`${cartPageUrl()}?sections=${sectionsParam}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;

    let payload;
    try {
      payload = await res.json();
    } catch (e) {
      return;
    }

    const sections = payload && payload.sections ? payload.sections : payload;
    applyCartPageSectionHtml(sections[ids.itemsSectionId], sections[ids.footerSectionId]);
  }

  async function publishCartUpdate(cartData, options) {
    const skipSectionRefresh = Boolean(options && options.skipSectionRefresh);
    if (isCartPageContext() && !skipSectionRefresh) {
      await refreshCartPageSections();
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: SOURCE,
        cartData,
      });
    }
  }

  function dispatchCartUpdated(cartData) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          source: SOURCE,
          cart: isFullCartPayload(cartData) ? cartData : null,
        },
      })
    );
  }

  async function evaluateCart(cartHint) {
    if (isEnforcing) {
      needsReevaluation = true;
      if (normalizeEventCart(cartHint)) pendingCartHint = cartHint;
      return;
    }

    const hintCart = normalizeEventCart(cartHint);
    let cart;
    try {
      cart = hintCart || (await getCart());
      if (hintCart) setCartCache(hintCart);
    } catch (e) {
      console.warn('[promo-campaigns]', e);
      return;
    }

    isEnforcing = true;
    let anyAdded = false;
    let anyChanged = false;
    let modalCampaign = null;
    chooseQueue = [];
    let sectionsApplied = false;

    try {
      let analyses = buildAnalyses(cart);
      const plan = buildMutationPlan(cart);
      if (plan.changed) {
        const mutationResult = await executeMutationPlan(cart, plan);
        cart = mutationResult.cart;
        sectionsApplied = mutationResult.sectionsApplied;
        anyChanged = true;
        anyAdded = plan.didAdd;
        modalCampaign = plan.modalCampaign;
        analyses = buildAnalyses(cart);
      }
      if (plan.chooseEntries.length) {
        chooseQueue = plan.chooseEntries.slice();
      }
      setCartCache(cart);
      if (anyChanged) {
        await publishCartUpdate(cart, { skipSectionRefresh: sectionsApplied });
        dispatchCartUpdated(cart);
      }
      updateUi(cart, analyses, { force: anyChanged && isCartPageContext() });
      if (anyAdded && modalCampaign) showModal(modalCampaign, cart, analyses);
      else showNextChooseModal();
    } catch (err) {
      console.warn('[promo-campaigns]', err);
    } finally {
      isEnforcing = false;
      if (needsReevaluation) {
        needsReevaluation = false;
        const hint = pendingCartHint;
        pendingCartHint = null;
        evaluateCart(hint);
      }
    }
  }

  function scheduleEvaluate(cartHint) {
    if (normalizeEventCart(cartHint)) pendingCartHint = cartHint;
    if (evaluateTimer) clearTimeout(evaluateTimer);
    evaluateTimer = setTimeout(() => {
      evaluateTimer = null;
      const hint = pendingCartHint;
      pendingCartHint = null;
      evaluateCart(hint);
    }, 50);
  }

  function cartHintFromCartUpdatedEvent(event) {
    if (!event || !event.detail) return null;
    if (event.detail.source === SOURCE) return null;
    return normalizeEventCart(event.detail.cart);
  }

  function initListeners() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return;
        const hint = normalizeEventCart(event && event.cartData);
        if (!hint) invalidateCartCache();
        scheduleEvaluate(hint);
      });
    }
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && event.detail.source === SOURCE) return;
      const hint = cartHintFromCartUpdatedEvent(event);
      if (!hint) invalidateCartCache();
      scheduleEvaluate(hint);
    });
  }

  function boot() {
    initListeners();
    evaluateCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
